/**
 * Browser client for the AI-gateway session stream bridge
 * (GET NDJSON + POST send/audio + DELETE).
 *
 * Does not log audio, transcripts, or tokens.
 */

import type { ClientFrame, ServerFrame } from "@/app/ai-agent/types";
import {
  AI_GATEWAY_STREAM_AUDIO_ENDPOINT_INSIDE,
  AI_GATEWAY_STREAM_ENDPOINT_INSIDE,
  AI_GATEWAY_STREAM_SEND_ENDPOINT_INSIDE,
} from "@/config";
import { ensureCsrfToken } from "@/lib/csrf-client";

export type StreamFrameHandler = (frame: ServerFrame) => void;

export type StreamDownlink = {
  /** Settles when the GET ends (abort, server close, or network error). */
  done: Promise<void>;
};

async function csrfHeaders(
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const csrfToken = await ensureCsrfToken();
  return {
    "x-csrf-token": csrfToken,
    ...extra,
  };
}

/**
 * Open the long-lived NDJSON downlink. Resolves once the response is OK and
 * the body reader is acquired (server subscriber is attached). Frame handling
 * continues in the background until `done` settles.
 */
export async function openStreamDownlink(opts: {
  conversationId: string;
  signal: AbortSignal;
  onFrame: StreamFrameHandler;
}): Promise<StreamDownlink> {
  const res = await fetch(
    `${AI_GATEWAY_STREAM_ENDPOINT_INSIDE}?conversationId=${encodeURIComponent(opts.conversationId)}`,
    {
      method: "GET",
      headers: await csrfHeaders(),
      signal: opts.signal,
    },
  );

  if (!res.ok || !res.body) {
    let message = "Couldn't connect to the assistant.";
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* non-JSON */
    }
    throw new Error(message);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  const done = (async () => {
    let buffer = "";
    while (true) {
      const { done: finished, value } = await reader.read();
      if (finished) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIndex = buffer.indexOf("\n");
      while (newlineIndex >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line) {
          try {
            opts.onFrame(JSON.parse(line) as ServerFrame);
          } catch {
            /* skip malformed */
          }
        }
        newlineIndex = buffer.indexOf("\n");
      }
    }
  })();

  return { done };
}

export async function sendStreamFrame(
  conversationId: string,
  frame: ClientFrame,
): Promise<void> {
  const res = await fetch(AI_GATEWAY_STREAM_SEND_ENDPOINT_INSIDE, {
    method: "POST",
    headers: await csrfHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ conversationId, frame }),
  });
  if (!res.ok) {
    let message = "Couldn't send to the assistant.";
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* non-JSON */
    }
    throw new Error(message);
  }
}

export async function sendStreamAudio(
  conversationId: string,
  pcm: ArrayBuffer,
): Promise<void> {
  if (pcm.byteLength === 0) return;
  const res = await fetch(
    `${AI_GATEWAY_STREAM_AUDIO_ENDPOINT_INSIDE}?conversationId=${encodeURIComponent(conversationId)}`,
    {
      method: "POST",
      headers: await csrfHeaders({
        "Content-Type": "application/octet-stream",
      }),
      body: pcm,
    },
  );
  if (!res.ok) {
    throw new Error("Couldn't send audio.");
  }
}

export async function closeStreamSession(
  conversationId: string,
): Promise<void> {
  try {
    await fetch(
      `${AI_GATEWAY_STREAM_ENDPOINT_INSIDE}?conversationId=${encodeURIComponent(conversationId)}`,
      {
        method: "DELETE",
        headers: await csrfHeaders(),
      },
    );
  } catch {
    /* best-effort close */
  }
}
